import React from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '../DropdownMenu';
import { Icon } from '../Icon';
import styles from './RowActionsMenu.module.css';

/**
 * Kebab-menu actions for a DataTable row.
 *
 * Renders a single ⋮ (three-dots) trigger that opens a Popover with the
 * provided actions. The action column header is conventionally empty
 * (`label: ''`) when this is used.
 *
 * Athena convention (Epic 11): every DataTable consumer uses this
 * instead of inline icon-button strips. Themis defines the same shape
 * per-page; Athena centralises it.
 *
 * Composes the 2.0 `DropdownMenu` family (trigger / content / items) — it owns
 * the menu surface, roving-focus keyboard nav, item paint, destructive variant,
 * and disabled state; this component just maps the `actions` array onto it and
 * keeps the row-click guard.
 *
 * Props:
 *   actions   — array of:
 *     {
 *       id:        unique key
 *       label:     menu text
 *       icon?:     ReactNode rendered to the left of the label
 *       onClick:   (event) => void; the menu closes before firing
 *       danger?:   boolean; styles the item red (for Delete etc.)
 *       disabled?: boolean
 *     }
 *   ariaLabel — trigger button aria-label (default 'Row actions')
 *   className — optional wrapper class
 */
const RowActionsMenu = ({ actions = [], ariaLabel = 'Row actions', className = '' }) => {
  if (actions.length === 0) return null;

  // The wrapper's stopPropagation guards the row's onRowClick: DropdownMenu
  // portals its panel, but React synthetic events still bubble to this React
  // ancestor, so trigger + item clicks are caught here before reaching the row.
  return (
    <div className={`${styles.wrap} ${className}`} onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger className={styles.trigger} aria-label={ariaLabel}>
          <Icon name="more" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="end">
          {actions.map((action) => (
            <DropdownMenuItem
              key={action.id}
              variant={action.danger ? 'destructive' : 'default'}
              disabled={action.disabled}
              onClick={(e) => {
                e.stopPropagation();
                action.onClick?.(e);
              }}
            >
              {action.icon}
              {action.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default RowActionsMenu;
